/**
 * AIAssistantWidget — Production-ready AI Assistant side panel
 *
 * Renders as a floating action button (closed) or a full-height
 * right-side panel (open) with smooth slide-in animation.
 *
 * Features:
 *  - Floating trigger button with subtle pulse
 *  - Full-height side panel (480px desktop, full-screen mobile)
 *  - Voice input (STT) via mic button
 *  - Conversation history drawer
 *  - Keyboard shortcut (Cmd+K / Ctrl+K)
 *  - Dark mode via Tailwind dark: variants
 *  - shadcn-inspired minimal aesthetic
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAIAssistant } from '../../hooks/useAIAssistant';
import useLinaProactive from '../../hooks/useLinaProactive';
import { useAuth } from '../../context/AuthContext';
import { detectLocale } from '../../config/accentMap';
import { cn } from '../../utils/cn';
import AIMessage from './AIMessage';
import AIInput from './AIInput';
import LinaProactiveToast from './LinaProactiveToast';

const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
const modKey = isMac ? '⌘' : 'Ctrl';

export default function AIAssistantWidget() {
  const {
    messages,
    isLoading,
    isStreaming,
    isOpen,
    error,
    conversationId,
    conversations,
    suggestions,
    statusText,
    messagesEndRef,
    sendMessage,
    stopStreaming,
    confirmAction,
    cancelAction,
    loadConversations,
    switchConversation,
    newConversation,
    deleteConversation,
    useSuggestion,
    submitFeedback,
    toggleOpen,
    close,
    clearMessages,
    setError,
  } = useAIAssistant();

  const { currentUser } = useAuth();

  const {
    notifications: proactiveNotifs,
    unreadCount: proactiveUnread,
    markRead: markProactiveRead,
    dismiss: dismissProactive,
    dismissAll: dismissAllProactive,
  } = useLinaProactive();

  const [showHistory, setShowHistory] = useState(false);
  const [historyQuery, setHistoryQuery] = useState('');
  const [showDraftHistory, setShowDraftHistory] = useState(false);
  const [isPeeking, setIsPeeking] = useState(false);
  const panelRef = useRef(null);
  const prevMsgCountRef = useRef(0);

  // Handler: when user clicks a suggested action in a proactive toast,
  // feed it into the AI chat and open the panel
  const handleProactiveAction = useCallback(({ label }) => {
    if (!isOpen) toggleOpen();
    sendMessage(label);
  }, [isOpen, toggleOpen, sendMessage]);

  // Load conversations when history opens
  useEffect(() => {
    if (showHistory) loadConversations();
  }, [showHistory, loadConversations]);

  // Auto-dismiss error
  useEffect(() => {
    if (error) {
      const t = setTimeout(() => setError(null), 6000);
      return () => clearTimeout(t);
    }
  }, [error, setError]);

  // ── Conversation history grouping ─────────────────────────────────────
  // Separates conversations into 'chat' (normal) and 'draft' (Draft with AI).
  // Each type is time-bucketed: Today / Yesterday / Previous 7 Days / …
  const { groupedChat, groupedDraft } = React.useMemo(() => {
    const makeBuckets = () => [
      { key: 'today', label: 'Today', items: [] },
      { key: 'yesterday', label: 'Yesterday', items: [] },
      { key: 'week', label: 'Previous 7 Days', items: [] },
      { key: 'month', label: 'Previous 30 Days', items: [] },
      { key: 'older', label: 'Older', items: [] },
    ];

    const chatBuckets = makeBuckets();
    const draftBuckets = makeBuckets();

    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const startOfYesterday = startOfToday - 86_400_000;
    const sevenDaysAgo = startOfToday - 7 * 86_400_000;
    const thirtyDaysAgo = startOfToday - 30 * 86_400_000;
    const q = historyQuery.trim().toLowerCase();

    const sorted = [...conversations].sort((a, b) => {
      const ta = new Date(a.lastActiveAt || a.updatedAt || a.createdAt || 0).getTime();
      const tb = new Date(b.lastActiveAt || b.updatedAt || b.createdAt || 0).getTime();
      return tb - ta;
    });

    for (const conv of sorted) {
      const title = (conv.title && conv.title !== 'New Conversation') ? conv.title : (conv.summary || 'Untitled chat');
      if (q && !title.toLowerCase().includes(q)) continue;
      const ts = new Date(conv.lastActiveAt || conv.updatedAt || conv.createdAt || 0).getTime();

      let bucket;
      const buckets = conv.source === 'draft' ? draftBuckets : chatBuckets;
      if (ts >= startOfToday) bucket = buckets[0];
      else if (ts >= startOfYesterday) bucket = buckets[1];
      else if (ts >= sevenDaysAgo) bucket = buckets[2];
      else if (ts >= thirtyDaysAgo) bucket = buckets[3];
      else bucket = buckets[4];
      bucket.items.push({ ...conv, _displayTitle: title, _ts: ts });
    }

    return {
      groupedChat: chatBuckets.filter((g) => g.items.length > 0),
      groupedDraft: draftBuckets.filter((g) => g.items.length > 0),
    };
  }, [conversations, historyQuery]);

  const formatRelativeTime = useCallback((ts) => {
    if (!ts) return '';
    const diff = Date.now() - ts;
    const min = Math.floor(diff / 60_000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.floor(hr / 24);
    if (day < 7) return `${day}d ago`;
    return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }, []);

  // Close on Escape — if peeking, first expand; if fully open, close
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape' && isOpen) {
        if (isPeeking) setIsPeeking(false);
        else close();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, isPeeking, close]);

  // Click anywhere outside the panel on desktop → collapse to peek strip
  useEffect(() => {
    if (!isOpen || isPeeking) return;
    const handler = (e) => {
      if (window.innerWidth < 640) return; // mobile uses backdrop close
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setIsPeeking(true);
      }
    };
    document.addEventListener('mousedown', handler, true);
    return () => document.removeEventListener('mousedown', handler, true);
  }, [isOpen, isPeeking]);

  // Auto-expand peek strip when AI sends a new reply
  useEffect(() => {
    if (messages.length > prevMsgCountRef.current) {
      const last = messages[messages.length - 1];
      if (last?.role === 'assistant' && isPeeking) setIsPeeking(false);
      prevMsgCountRef.current = messages.length;
    }
  }, [messages, isPeeking]);

  // Reset peek state when panel closes
  useEffect(() => {
    if (!isOpen) setIsPeeking(false);
  }, [isOpen]);

  /* ── Closed State: Floating Button ──────────────────────────────────── */

  if (!isOpen) {
    return (
      <div className="fixed bottom-5 right-5 z-50 group">
        {/* Tooltip */}
        <div className="absolute bottom-full right-0 mb-3 w-56 px-3.5 py-2.5 rounded-xl
          bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700
          shadow-lg opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0
          transition-all duration-200 pointer-events-none"
        >
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Tasklina AI</p>
          <p className="text-xs text-gray-500 dark:text-gray-300 mt-0.5">
            Ask anything about your tasks
          </p>
          <p className="text-[10px] text-gray-400 dark:text-gray-400 mt-1.5">
            <kbd className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-[10px] font-mono border border-gray-200 dark:border-gray-700">
              {modKey}+K
            </kbd>{' '}
            to toggle
          </p>
          <div className="absolute right-5 -bottom-1 w-2 h-2 bg-white dark:bg-gray-900 border-r border-b border-gray-200 dark:border-gray-700 rotate-45" />
        </div>

        {/* Ring pulse */}
        <div className="absolute inset-0 w-12 h-12 rounded-full bg-blue-500/15 animate-ping" />

        {/* Unread proactive badge */}
        {proactiveUnread > 0 && (
          <span className="absolute -top-1 -right-1 z-10 flex items-center justify-center min-w-[20px] h-5 px-1
            rounded-full bg-red-500 text-white text-[10px] font-bold shadow-lg ring-2 ring-white dark:ring-gray-950
            animate-bounce-subtle">
            {proactiveUnread > 9 ? '9+' : proactiveUnread}
          </span>
        )}

        {/* Button */}
        <button
          onClick={toggleOpen}
          className="relative w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600
            shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30 hover:scale-105
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2
            transition-all duration-200"
          aria-label="Open AI Assistant"
        >
          <div className="w-full h-full flex items-center justify-center overflow-hidden rounded-full">
            <img src="/linaai.png" alt="Tasklina AI" className="w-14 h-14 rounded-full object-cover" />
          </div>
        </button>

        {/* Proactive toasts when widget is closed */}
        <LinaProactiveToast
          notifications={proactiveNotifs}
          onDismiss={dismissProactive}
          onMarkRead={markProactiveRead}
          onAction={handleProactiveAction}
          onDismissAll={dismissAllProactive}
        />
      </div>
    );
  }

  /* ── Open State: Side Panel ─────────────────────────────────────────── */

  return (
    <>
      {/* Backdrop overlay for mobile */}
      <div
        className="fixed inset-0 z-40 bg-black/20 backdrop-blur-[2px] sm:hidden animate-fade-in"
        onClick={close}
      />

      {/* Panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-label="Tasklina AI Assistant"
        aria-modal="true"
        title={isPeeking ? 'Click to expand Tasklina AI' : undefined}
        className={cn(
          'fixed z-50 flex flex-col',
          // Mobile: always full-screen
          'inset-0',
          // Desktop: transition between full panel and peek strip
          isPeeking
            ? 'sm:inset-y-0 sm:left-auto sm:right-0 sm:w-14 sm:cursor-pointer'
            : 'sm:inset-y-0 sm:left-auto sm:right-0 sm:w-[480px] sm:max-w-[calc(100vw-4rem)]',
          // Background
          isPeeking
            ? 'bg-white/90 dark:bg-gray-950/90 backdrop-blur-md'
            : 'bg-gradient-to-b from-white via-white to-gray-50/80 dark:from-gray-950 dark:via-gray-950 dark:to-gray-900/80',
          'border-l border-gray-200 dark:border-gray-800 shadow-2xl',
          // Smooth width transition (desktop only)
          'transition-[width] duration-300 ease-in-out',
        )}
        onClick={isPeeking ? () => setIsPeeking(false) : undefined}
      >
        {/* ── Peek strip: desktop-only collapsed state ─────────────────── */}
        <div className={cn(
          'flex-col items-center justify-between py-8 h-full select-none',
          isPeeking ? 'hidden sm:flex' : 'hidden',
        )}>
          {/* Avatar + status dot */}
          <div className="flex flex-col items-center gap-2.5">
            <div className="w-9 h-9 rounded-full overflow-hidden ring-2 ring-blue-400/30 ring-offset-2 ring-offset-white dark:ring-offset-gray-950">
              <img src="/linaai.png" alt="Tasklina AI" className="w-full h-full object-cover" />
            </div>
            <div className={cn(
              'w-2 h-2 rounded-full',
              isStreaming || isLoading ? 'bg-amber-400 animate-pulse' : 'bg-emerald-500'
            )} />
          </div>

          {/* Vertical label */}
          <span
            className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-[0.12em]"
            style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
          >
            Lina AI
          </span>

          {/* Message count badge + expand chevron */}
          <div className="flex flex-col items-center gap-2">
            {messages.length > 0 && (
              <span className="w-6 h-6 rounded-full bg-blue-500 text-white text-[9px] font-bold flex items-center justify-center shadow-md">
                {messages.length > 9 ? '9+' : messages.length}
              </span>
            )}
            <svg className="w-4 h-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </div>
        </div>

        {/* ── Full panel content (always visible on mobile; hidden on desktop when peeking) ── */}
        <div className={cn('flex flex-col flex-1 min-h-0 overflow-hidden', isPeeking && 'sm:hidden')}>

        {/* ── Header ────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200/80 dark:border-gray-800/80 bg-white/80 dark:bg-gray-950/80 backdrop-blur-sm flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0 overflow-hidden">
              <img src="/linaai.png" alt="Tasklina" className="w-11 h-11 rounded-full object-cover" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-tight">
                Tasklina AI
              </h2>
              <div className="flex items-center gap-1.5">
                <div className={cn(
                  'w-1.5 h-1.5 rounded-full',
                  isStreaming || isLoading ? 'bg-amber-400 animate-pulse' : 'bg-emerald-500'
                )} />
                <p className="text-xs text-gray-500 dark:text-gray-300 leading-tight">
                  {statusText
                    ? statusText
                    : isStreaming ? 'Typing…' : isLoading ? 'Thinking…' : 'Online'}
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-0.5">
            {/* New conversation */}
            <button
              onClick={newConversation}
              className="h-8 w-8 rounded-lg flex items-center justify-center text-gray-500 dark:text-gray-300
                hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-200
                transition-colors duration-150"
              title="New conversation"
              aria-label="New conversation"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
            </button>

            {/* History */}
            <button
              onClick={() => setShowHistory(!showHistory)}
              className={cn(
                'h-8 w-8 rounded-lg flex items-center justify-center transition-colors duration-150',
                showHistory
                  ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100'
                  : 'text-gray-500 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-200'
              )}
              title="Conversation history"
              aria-label="Conversation history"
              aria-expanded={showHistory}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>

            {/* Clear chat */}
            {messages.length > 0 && (
              <button
                onClick={clearMessages}
                className="h-8 w-8 rounded-lg flex items-center justify-center text-gray-500 dark:text-gray-300
                  hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-200
                  transition-colors duration-150"
                title="Clear chat"
                aria-label="Clear chat"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                </svg>
              </button>
            )}

            {/* Close */}
            <button
              onClick={close}
              className="h-8 w-8 rounded-lg flex items-center justify-center text-gray-500 dark:text-gray-300
                hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-200
                transition-colors duration-150 ml-1"
              title={`Close (${modKey}+K)`}
              aria-label="Close assistant"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* ── Conversation History Drawer ────────────────────────────────── */}
        {showHistory && (
          <div className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/60
            flex flex-col flex-shrink-0 animate-slide-in-from-top-2"
            style={{ maxHeight: '60vh' }}
          >
            {/* Header + search */}
            <div className="px-4 pt-3 pb-2 flex-shrink-0 border-b border-gray-200/70 dark:border-gray-800/70">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[11px] font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Chat History
                </p>
                <span className="text-[10.5px] text-gray-400 dark:text-gray-500">
                  {conversations.filter(c => c.source !== 'draft').length} chats
                </span>
              </div>
              <div className="relative">
                <svg className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="7" />
                  <path d="M21 21l-4.3-4.3" strokeLinecap="round" />
                </svg>
                <input
                  type="text"
                  value={historyQuery}
                  onChange={(e) => setHistoryQuery(e.target.value)}
                  placeholder="Search chats…"
                  className="w-full pl-8 pr-2 py-1.5 text-xs rounded-lg bg-white dark:bg-gray-800
                    border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-100
                    placeholder:text-gray-400 dark:placeholder:text-gray-500
                    focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                />
              </div>
            </div>

            {/* Chat history list */}
            <div className="flex-1 overflow-y-auto px-2 py-2">
              {conversations.filter(c => c.source !== 'draft').length === 0 ? (
                <p className="text-xs text-gray-400 dark:text-gray-400 py-4 text-center">
                  No previous chats
                </p>
              ) : groupedChat.length === 0 ? (
                <p className="text-xs text-gray-400 dark:text-gray-400 py-4 text-center">
                  No chats match &ldquo;{historyQuery}&rdquo;
                </p>
              ) : (
                groupedChat.map((group) => (
                  <div key={group.key} className="mb-3 last:mb-0">
                    <p className="px-2 mb-1 text-[10.5px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                      {group.label}
                    </p>
                    <div className="space-y-0.5">
                      {group.items.map((conv) => {
                        const isActive = conversationId === conv._id;
                        return (
                          <div
                            key={conv._id}
                            role="button"
                            tabIndex={0}
                            onClick={() => { switchConversation(conv._id); setShowHistory(false); }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                switchConversation(conv._id);
                                setShowHistory(false);
                              }
                            }}
                            className={cn(
                              'group flex items-start gap-2 px-2.5 py-2 rounded-lg cursor-pointer transition-colors',
                              isActive
                                ? 'bg-blue-50 dark:bg-blue-900/25 text-blue-700 dark:text-blue-200'
                                : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200'
                            )}
                          >
                            <svg className={cn(
                              'w-3.5 h-3.5 flex-shrink-0 mt-0.5',
                              isActive ? 'text-blue-500 dark:text-blue-300' : 'text-gray-400 dark:text-gray-500'
                            )} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a8.5 8.5 0 11-3.2-6.6L21 4l-1 4.4A8.46 8.46 0 0121 12z" />
                            </svg>
                            <div className="flex-1 min-w-0">
                              <p className="truncate text-xs font-medium leading-snug">{conv._displayTitle}</p>
                              <p className="text-[10.5px] text-gray-400 dark:text-gray-500 mt-0.5">
                                {formatRelativeTime(conv._ts)}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); deleteConversation(conv._id); }}
                              title="Delete chat"
                              aria-label={`Delete ${conv._displayTitle}`}
                              className="flex-shrink-0 h-6 w-6 rounded flex items-center justify-center
                                opacity-0 group-hover:opacity-100 hover:bg-red-100 dark:hover:bg-red-900/30 transition-opacity"
                            >
                              <svg className="w-3 h-3 text-gray-400 hover:text-red-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.9 12.1a2 2 0 01-2 1.9H7.9a2 2 0 01-2-1.9L5 7m5 4v6m4-6v6M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3m-9 0h14" />
                              </svg>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}

              {/* ── Draft Sessions collapsible section ─────────────────── */}
              {(groupedDraft.length > 0 || conversations.some(c => c.source === 'draft')) && (
                <div className="mt-3 border-t border-gray-200/70 dark:border-gray-700/50 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowDraftHistory((v) => !v)}
                    className="w-full flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-left
                      text-[10.5px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider
                      hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors group"
                  >
                    {/* Chevron */}
                    <svg
                      className={cn('w-3 h-3 flex-shrink-0 transition-transform', showDraftHistory ? 'rotate-90' : '')}
                      fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                    {/* Sparkle / document icon */}
                    <svg className="w-3 h-3 flex-shrink-0 text-purple-400 dark:text-purple-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.3 24.3 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15M14.25 3.104c.251.023.501.05.75.082M19.8 15l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23-.607L5 14.5m14.8.5-3.78 3.78a1 1 0 01-1.415 0L12 16.06l-2.605 2.72a1 1 0 01-1.415 0L4.2 15" />
                    </svg>
                    <span>Draft Sessions</span>
                    <span className="ml-auto text-[10px] font-normal normal-case tracking-normal text-gray-400 dark:text-gray-600">
                      {conversations.filter(c => c.source === 'draft').length}
                    </span>
                  </button>

                  {showDraftHistory && (
                    <div className="mt-1">
                      {groupedDraft.length === 0 ? (
                        <p className="text-xs text-gray-400 dark:text-gray-500 py-2 px-2">No draft sessions</p>
                      ) : (
                        groupedDraft.map((group) => (
                          <div key={group.key} className="mb-2 last:mb-0">
                            <p className="px-2 mb-0.5 text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                              {group.label}
                            </p>
                            <div className="space-y-0.5">
                              {group.items.map((conv) => {
                                const isActive = conversationId === conv._id;
                                return (
                                  <div
                                    key={conv._id}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => { switchConversation(conv._id); setShowHistory(false); }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        switchConversation(conv._id);
                                        setShowHistory(false);
                                      }
                                    }}
                                    className={cn(
                                      'group flex items-start gap-2 px-2.5 py-1.5 rounded-lg cursor-pointer transition-colors',
                                      isActive
                                        ? 'bg-purple-50 dark:bg-purple-900/25 text-purple-700 dark:text-purple-200'
                                        : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300'
                                    )}
                                  >
                                    <svg className={cn(
                                      'w-3 h-3 flex-shrink-0 mt-0.5',
                                      isActive ? 'text-purple-500 dark:text-purple-300' : 'text-purple-300 dark:text-purple-600'
                                    )} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                                    </svg>
                                    <div className="flex-1 min-w-0">
                                      <p className="truncate text-[11px] font-medium leading-snug">{conv._displayTitle}</p>
                                      <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">
                                        {formatRelativeTime(conv._ts)}
                                      </p>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); deleteConversation(conv._id); }}
                                      title="Delete draft session"
                                      aria-label={`Delete ${conv._displayTitle}`}
                                      className="flex-shrink-0 h-5 w-5 rounded flex items-center justify-center
                                        opacity-0 group-hover:opacity-100 hover:bg-red-100 dark:hover:bg-red-900/30 transition-opacity"
                                    >
                                      <svg className="w-2.5 h-2.5 text-gray-400 hover:text-red-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.9 12.1a2 2 0 01-2 1.9H7.9a2 2 0 01-2-1.9L5 7m5 4v6m4-6v6M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3m-9 0h14" />
                                      </svg>
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Error Banner ──────────────────────────────────────────────── */}
        {error && (
          <div className="flex items-center gap-2 px-4 py-2.5 bg-red-50 dark:bg-red-950/30
            border-b border-red-100 dark:border-red-900/50 text-sm text-red-600 dark:text-red-400
            animate-slide-in-from-top-1 flex-shrink-0"
          >
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126z" />
            </svg>
            <span className="flex-1 text-xs">{error}</span>
            <button
              onClick={() => setError(null)}
              className="flex-shrink-0 hover:text-red-800 dark:hover:text-red-200"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* ── Messages Area ─────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto px-4 py-5 min-h-0
          scrollbar-none [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
        >
          {messages.length === 0 ? (
            /* Empty state */
            <div className="flex flex-col items-center justify-center h-full text-center px-6">
              <div className="relative mb-6">
                <div className="absolute inset-0 w-24 h-24 rounded-full bg-gradient-to-br from-blue-400/20 to-indigo-400/20 dark:from-blue-500/10 dark:to-indigo-500/10 blur-xl animate-pulse" />
                <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 border border-gray-200/60 dark:border-gray-700/60 flex items-center justify-center shadow-lg shadow-blue-500/5 overflow-hidden">
                  <img src="/linaai.png" alt="Tasklina AI" className="w-28 h-28 rounded-full object-cover" />
                </div>
              </div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2">
                How can I help?
              </h3>
              <p className="text-[13px] text-gray-500 dark:text-gray-300 max-w-[280px] leading-relaxed mb-5">
                I can manage your tasks, search projects, set deadlines, and more.
              </p>
              {/* Starter suggestion chips */}
              {suggestions.length > 0 && (
                <div className="flex flex-wrap justify-center gap-2 max-w-[340px]">
                  {suggestions.map((item, i) => {
                    const label = typeof item === 'string' ? item : item?.label || item?.prompt;
                    return (
                      <button
                        key={i}
                        onClick={() => useSuggestion(item)}
                        disabled={isLoading || isStreaming}
                        className={cn(
                          'px-3.5 py-2 text-[12px] font-medium rounded-xl',
                          'bg-white dark:bg-gray-800/80',
                          'border border-gray-200/80 dark:border-gray-700/60',
                          'text-gray-600 dark:text-gray-200',
                          'hover:border-primary-300 dark:hover:border-primary-600',
                          'hover:bg-primary-50/60 dark:hover:bg-primary-950/30',
                          'hover:text-primary-700 dark:hover:text-primary-300',
                          'shadow-sm hover:shadow-md',
                          'transition-all duration-150',
                          'disabled:opacity-40 disabled:cursor-not-allowed'
                        )}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-0.5">
              {messages.map((msg, idx) => (
                <AIMessage
                  key={msg.id}
                  message={msg}
                  messageIndex={idx}
                  conversationId={conversationId}
                  onConfirmAction={confirmAction}
                  onCancelAction={cancelAction}
                  onFeedback={submitFeedback}
                  onRetry={msg.role === 'assistant' ? () => {
                    // Find the user message that preceded this assistant message
                    const prevUserMsg = messages.slice(0, idx).reverse().find(m => m.role === 'user');
                    if (prevUserMsg?.content) sendMessage(prevUserMsg.content);
                  } : undefined}
                  isLoading={isLoading}
                  followUps={msg.followUps}
                  onFollowUpClick={useSuggestion}
                  isStreaming={isStreaming}
                  userProfileImage={currentUser?.profileImage}
                  userName={currentUser?.name}
                />
              ))}
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* ── Input ─────────────────────────────────────────────────────── */}
        <AIInput
          onSend={sendMessage}
          onStop={stopStreaming}
          isLoading={isLoading}
          isStreaming={isStreaming}
          voiceLocale={detectLocale()}
        />
        </div>{/* end full panel content */}
      </div>
    </>
  );
}
